import { TriangleAlert } from 'lucide-react';
import { t } from '../lib/i18n';
import { Button } from './ui/button';
import type { BlockingKind } from '../lib/bootState';

/**
 * A full-screen dead end with a NAME. A spinner that never ends says none of
 * what these five say.
 *
 * The union lives in lib/bootState.ts with the decision that picks a kind --
 * pure code owns the vocabulary, the component renders it -- and is re-exported
 * here so a caller that only needs the renderer has one import.
 */
export type { BlockingKind };

/**
 * Copy is resolved through literal t() calls, one per kind: a computed key
 * would still render, but test/i18nKeys.test.ts could no longer prove the copy
 * exists, and a typo would ship as a visible key.
 */
function copyFor(kind: BlockingKind): { title: string; body: string } {
  switch (kind) {
    case 'network':
      return { title: t('blocking.network.title'), body: t('blocking.network.body') };
    case 'deployment':
      return { title: t('blocking.deployment.title'), body: t('blocking.deployment.body') };
    case 'platform':
      return { title: t('blocking.platform.title'), body: t('blocking.platform.body') };
    case 'turnstileMissing':
      return { title: t('blocking.turnstileMissing.title'), body: t('blocking.turnstileMissing.body') };
    case 'generic':
      return { title: t('blocking.generic.title'), body: t('blocking.generic.body') };
  }
}

export function Blocking({ kind, onRetry }: { kind: BlockingKind; onRetry?: () => void }) {
  const { title, body } = copyFor(kind);
  return (
    <div
      className="blocking grid min-h-screen place-items-center bg-bg px-4 py-10"
      role="alert"
      data-kind={kind}
    >
      <div className="w-full max-w-[26rem] rounded-card border border-line bg-surface p-6 shadow-float">
        <span
          aria-hidden="true"
          className="mb-4 grid size-9 place-items-center rounded-[0.5rem] border border-warn/35 bg-warn-soft text-warn"
        >
          <TriangleAlert className="size-[1.0625rem]" />
        </span>
        <h1 className="text-[1.0625rem]">{title}</h1>
        <p className="mt-1.5 text-[0.8125rem] leading-relaxed text-muted">{body}</p>
        {onRetry ? (
          <Button variant="default" className="mt-5" onClick={onRetry}>
            {t('blocking.retry')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
